import { graphql } from '@/utils/graphql';
import { logger } from '@/utils/logger';

const ns = 'app.services.dashboardFilterPersistence';

export type FilterOptionsMap = Record<string, unknown>;

const recordExistsCache = new Map<string, boolean>();

function compositeKey(userId: string, dashboardId: string): string {
  return `${userId}#${dashboardId}`;
}

async function load(userId: string, dashboardId: string): Promise<FilterOptionsMap | null> {
  const key = compositeKey(userId, dashboardId);
  try {
    const data = await graphql('getUserPreferenceDashboard', { id: key });
    if (!data) {
      recordExistsCache.set(key, false);
      return null;
    }
    recordExistsCache.set(key, true);
    try {
      const parsed = JSON.parse(data.options);
      if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
        return parsed as FilterOptionsMap;
      }
      return null;
    } catch {
      logger.debug({ label: `${ns}.load.parseError`, message: key });
      return null;
    }
  } catch (err) {
    logger.error({ label: `${ns}.load.error`, message: err });
    return null;
  }
}

async function save(userId: string, dashboardId: string, options: FilterOptionsMap): Promise<void> {
  const key = compositeKey(userId, dashboardId);
  try {
    const serialised = JSON.stringify(options);
    const exists = recordExistsCache.get(key);
    if (exists) {
      await graphql('updateUserPreferenceDashboard', {
        input: { id: key, options: serialised },
      });
    } else {
      await graphql('createUserPreferenceDashboard', {
        input: { id: key, userId, dashboardId, options: serialised },
      });
      recordExistsCache.set(key, true);
    }
  } catch (err) {
    logger.error({ label: `${ns}.save.error`, message: err });
  }
}

function clearCache(userId: string, dashboardId: string): void {
  recordExistsCache.delete(compositeKey(userId, dashboardId));
}

const DashboardFilterPersistence = { load, save, clearCache };

export default DashboardFilterPersistence;
